/**
 * seedTestMastermind
 *
 * Maakt een test-Mastermind aan voor de huidige ingelogde gebruiker.
 * Voegt eventuele andere gevonden test accounts toe als leden.
 *
 * Veilig om meerdere keren uit te voeren — controleert op bestaand record.
 */

import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

interface SeedResult {
  success: boolean;
  message: string;
  entityId?: string;
}

export async function seedTestMastermind(): Promise<SeedResult> {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { success: false, message: 'Je moet ingelogd zijn om dit te doen.' };
    }

    const ownerUid   = currentUser.uid;
    const ownerEmail = currentUser.email ?? 'onbekend';
    const ownerName  = currentUser.displayName ?? ownerEmail;

    console.log(`[Seed] Start voor: ${ownerEmail} (${ownerUid})`);

    // ── 1. Controleer of al een mastermind bestaat voor deze gebruiker ──
    const existingQ = query(
      collection(db, 'entities'),
      where('ownerId', '==', ownerUid),
      where('type', '==', 'mastermind'),
      limit(1),
    );
    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) {
      const existingId = existingSnap.docs[0].id;
      console.log(`[Seed] Mastermind bestaat al: ${existingId}`);
      return {
        success: true,
        message: `✅ Mastermind bestond al. Ververs de pagina als je hem nog niet ziet.`,
        entityId: existingId,
      };
    }

    // ── 2. Maak 'entities' document aan ─────────────────────────────
    const entityRef = await addDoc(collection(db, 'entities'), {
      type: 'mastermind',
      name: 'SuperKonnected Test Mastermind',
      description: 'Test groep voor de Structured Growth Engine.',
      ownerId: ownerUid,
      ownerName: ownerName,
      ownerEmail: ownerEmail,
      isPublic: false,
      visibility: 'private',
      requiresSubscription: false,
      ownerHasSubscription: true,
      memberCount: 1,
      maxMembers: 10,
      isActive: true,
      isVisible: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const entityId = entityRef.id;
    console.log(`[Seed] Entity aangemaakt: ${entityId}`);

    // ── 3. Voeg huidige gebruiker toe als owner ──────────────────────
    await setDoc(doc(db, 'entityMembers', `${entityId}_${ownerUid}`), {
      entityId,
      userId: ownerUid,
      role: 'owner',
      createdAt: serverTimestamp(),
      createdBy: ownerUid,
    });

    // ── 4. Voeg andere test accounts toe als lid (als ze bestaan) ────
    const otherTestEmails = [
      'test@superkonnected.nl',
      'test1@superkonnected.nl',
    ].filter((e) => e !== ownerEmail);

    let extraMembers = 0;
    for (const email of otherTestEmails) {
      const q = query(
        collection(db, 'users'),
        where('email', '==', email),
        limit(1),
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const uid = snap.docs[0].id;
        await setDoc(doc(db, 'entityMembers', `${entityId}_${uid}`), {
          entityId,
          userId: uid,
          role: 'member',
          createdAt: serverTimestamp(),
          createdBy: ownerUid,
        });
        extraMembers++;
        console.log(`[Seed] Lid toegevoegd: ${email}`);
      } else {
        console.log(`[Seed] Overgeslagen (niet gevonden): ${email}`);
      }
    }

    // ── 5. Maak networkClubs document aan ────────────────────────────
    await addDoc(collection(db, 'networkClubs'), {
      name: 'SuperKonnected Test Mastermind',
      description: 'Test groep voor de Structured Growth Engine.',
      type: 'mastermind',
      entityId,
      organizerId: ownerUid,
      organizerName: ownerName,
      organizerEmail: ownerEmail,
      visibility: 'private',
      isPublic: false,
      isActive: true,
      isVisible: false,
      memberCount: 1 + extraMembers,
      minMembersRequired: 5,
      moderationStatus: 'approved',
      isVerified: false,
      scope: 'nationaal',
      country: 'Nederland',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: `✅ Test-Mastermind aangemaakt! Pagina wordt herladen...`,
      entityId,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('[Seed] Fout:', error);
    return { success: false, message: `Fout: ${msg}` };
  }
}
